import React, { useEffect, useState } from 'react';
import { Box, Typography, TextField, Button, Container, Grid, CircularProgress } from '@mui/material';
import { TopBar } from '../../top_bar/TopBar';
import './CompleteInterview.css'
import '../../../styles/Text.css'
import Confetti from "react-confetti";
import axios from 'axios';

export default function CompleteInterview({ isB2BInterview, jobListingId, candidateEmail, resumeData, candidateFirstName, candidateLastName }) {
    const [redirectUrl, setRedirectUrl] = useState(null);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        firstName: candidateFirstName || '',
        lastName: candidateLastName || '',
        email: candidateEmail || '',
        phone: '',
        resume: resumeData || ''
    });
    
    const [applicationId, setApplicationId] = useState(null);
    
    useEffect(() => {
        // If it's a B2C interview, redirect to feedback page after a delay
        if (!isB2BInterview && !isFormSubmitted) {
            // No need to do anything for B2C, the Footer component handles the redirect
        }
    }, [isB2BInterview, isFormSubmitted]);

    // Update the form data when props change
    useEffect(() => {
        setFormData(prev => ({
            ...prev,
            firstName: candidateFirstName || prev.firstName,
            lastName: candidateLastName || prev.lastName,
            email: candidateEmail || prev.email,
            resume: resumeData || prev.resume
        }));
    }, [candidateFirstName, candidateLastName, candidateEmail, resumeData]);
    
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            // Get interview data from sessionStorage
            const interviewData = JSON.parse(sessionStorage.getItem('interviewData') || '[]');
            const chatHistory = JSON.parse(sessionStorage.getItem('chatHistory') || '[]');
            const storedJobListingId = sessionStorage.getItem('jobListingId') || jobListingId;
            
            if (!storedJobListingId) {
                console.error("No job listing ID available");
                return;
            }
            
            // Create or get user and application
            const response = await axios.post(`${process.env.REACT_APP_B2B_BACKEND_URL}/applications/submit-interview`, {
                jobListingId: storedJobListingId,
                candidateDetails: {
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    email: formData.email,
                    phone: formData.phone
                },
                resume: formData.resume,
                interviewData,
                chatHistory
            });
            
            if (response.data && response.data.applicationId) {
                setApplicationId(response.data.applicationId);
                setIsFormSubmitted(true);
                
                // Redirect to results page after 3 seconds
                setTimeout(() => {
                    window.location.href = `${process.env.REACT_APP_B2B_FRONTEND_URL}/applications/${response.data.applicationId}/results`;
                }, 3000);
            }
        } catch (error) {
            console.error("Error submitting application:", error);
        } finally {
            setLoading(false);
        }
    };

    const B2BApplicantForm = (
        <Container maxWidth="md" className="application-form">
            <Typography variant="h5" component="h2" align="center" gutterBottom>
                Complete Your Application
            </Typography>
            <Typography variant="body1" align="center" paragraph>
                Thanks for completing the interview! Please review your details and submit your application.
            </Typography>
            
            <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            label="First Name"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                            disabled={!!candidateFirstName}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            required
                            fullWidth
                            label="Last Name"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                            disabled={!!candidateLastName}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            required
                            fullWidth
                            label="Email Address"
                            name="email"
                            type="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            disabled={!!candidateEmail}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Phone Number"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            required
                            fullWidth
                            label="Resume/CV"
                            name="resume"
                            multiline
                            rows={6}
                            value={formData.resume}
                            onChange={handleInputChange}
                            placeholder="Paste your resume or CV here"
                        />
                    </Grid>
                    <Grid item xs={12} style={{ textAlign: 'center' }}>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={loading || !formData.resume}
                            className="main-button-middle"
                        >
                            {loading ? <CircularProgress size={24} /> : "Submit Application"}
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Container>
    );

    const ThankYouMessage = (
        <>
            <Box className="complete-interview-image-box">      
                <img
                    src='/icons/feedback2.svg'
                    alt="feedback"
                    loading="lazy"
                />
            </Box>

            <Box className="large-text-container">      
                <Typography className='title-text-thin' style={{marginBottom: '15px'}}> 
                    {isB2BInterview ? 'Thank you for completing the interview!' : 'Gathering Feedback ...'} 
                </Typography>
                
                {isB2BInterview && (
                    <Typography component="span" className="small-grey-text" style={{marginBottom: '3%', width: '60%', textAlign: 'center'}}>
                        Your application has been successfully submitted. You will be redirected to the results page shortly.
                    </Typography>
                )}
            </Box>
        </>
    );

    return (
        <>
            <TopBar />
            <Box className="complete-interview">         
                {isFormSubmitted && <Confetti />}
                
                {isB2BInterview && !isFormSubmitted ? (
                    B2BApplicantForm
                ) : (
                    ThankYouMessage
                )}
            </Box>
        </>
    );
}

